import { track } from '@sloyd/analytics';
import * as api from '@sloyd/api';
import { actions, selectors } from '@sloyd/store-base';
import { toast } from '@sloyd/ui';
import { clone } from 'lodash';
import { call, delay, fork, put, select, take, takeEvery } from 'saga-ts';
import { $a, guid4, invokeEvent, l } from 'shared-base';
import { dataGuard } from './helpers/guards';
import { navToPlayground } from './helpers/navigation';
import { preparePlayground } from './helpers/playground';
import { chooseProject } from './helpers/projects';

export const saga = {
  id: 'sloyd.playground',
  type: 'entity',
  root: root,
  trigger: {
    actionTypes: ['PLAYGROUND'],
  },
};

type Verb =
  | 'zoom' //
  | 'create'
  | 'browseIdeas'
  | 'saveToProject'
  | 'loadGenerator'
  | 'loadAssetPack';

type Action = {
  type: 'PLAYGROUND';
  verb: Verb;
  id: string;
  payload: Json;
};

const analyitcsMapPlayground: Record<Verb, any> = {
  zoom: 'Zoom',
  create: 'Create Playground',
  saveToProject: 'Save Model To Project',
  loadGenerator: 'Load Generator',
  loadAssetPack: 'Load Asset Pack',
  browseIdeas: 'Browse Ideas',
};

const map: Record<Verb, any> = {
  loadAssetPack: loadAssetPack,
  loadGenerator: loadGenerator,
  saveToProject: saveToProject,
  create: create,
  zoom: zoom,
  browseIdeas: browseIdeas,
};

export function* browseIdeas(action: Action) {
  const { id } = action;
  try {
    const res = yield api.ideas.get(id);
    delete res.data.projectId;

    yield call(preparePlayground);
    yield delay(100);

    const modelId = guid4();

    yield put(
      $a('MODELS', 'create', '', {
        id: modelId,
        ...res.data,
      })
    );

    yield navToPlayground();
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* saveToProject(action: Action) {
  try {
    const projectId = yield* call(chooseProject);
    if (!projectId) return;

    const modelSource = yield* select(selectors.single.$currentModel);
    const project = yield* select(selectors.single.$project, projectId);

    if (!modelSource || !project) {
      return;
    }

    const model: any = clone(modelSource);

    const modelId = guid4();
    delete model.id;

    yield put(
      $a('MODELS', 'create', modelId, {
        ...model,
        projectId,
      })
    );

    toast.show('Model saved to project');
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* create(action: Action) {
  try {
    yield call(preparePlayground);
    yield delay(50);

    const playground = yield* select(selectors.base.$playground);

    if (!playground) {
      return;
    }

    yield delay(300);
    yield navToPlayground();
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* zoom(_action: Action) {
  try {
    const playground = yield* select(selectors.base.$playground);

    if (!playground) {
      return;
    }

    yield put(actions.currentIds.patch({ projectId: playground.id }));
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* loadAssetPack(action: Action) {
  try {
    const { id: packId } = action;

    yield call(preparePlayground);
    yield delay(50);

    const playground = yield* select(selectors.base.$playground);
    const assetPack = yield* select(selectors.single.$assetPack, packId);

    if (!assetPack || !playground) {
      return;
    }

    yield put($a('PACKS', 'load', packId));

    yield take('PACK_LOADED');

    yield navToPlayground();
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* loadGenerator(action: Action) {
  try {
    const { id: generatorId } = action;

    yield call(preparePlayground);
    yield delay(100);

    const playground = yield* select(selectors.base.$playground);
    const generator = yield* select(selectors.single.$generator, generatorId);

    if (!generator || !playground) {
      return;
    }

    const { name, paletteId, slots } = generator;
    const { spawnPack } = slots ?? {};

    const modelId = guid4();

    yield put(
      $a('MODELS', 'create', '', {
        id: modelId,
        name,
        generatorId,
        projectId: playground.id,
        paletteId,
        spawnPack,
      })
    );

    yield navToPlayground();
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* playground(action: any) {
  const { verb, id } = action;
  yield delay(10);

  yield* dataGuard();

  const saga = (map as any)[verb];

  if (!saga) {
    return;
  }

  l(`saga|saga.playground ${verb} on ${id}`);
  track((analyitcsMapPlayground as any)[verb], { origin, id });

  yield* fork(saga, action);
}

export function* root() {
  yield takeEvery('PLAYGROUND', playground);
}
